/*
 * Sistema de Lazy Loading Optimizado para Shopify Pipeline Theme
 * 
 * Este archivo contiene todas las optimizaciones de lazy loading
 * para mejorar la performance de carga de imágenes en la home page
 * y en todo el sitio.
 */

(function() {
  'use strict';

  // ===== SISTEMA DE LAZY LOADING OPTIMIZADO =====
  
  class LazyImageLoader {
    constructor() {
      this.imageObserver = null;
      this.init();
    }

    init() {
      // Verificar soporte para WebP
      this.checkWebPSupport();
      
      // Inicializar Intersection Observer para lazy loading
      this.initIntersectionObserver();
      
      // Manejar imágenes ya cargadas
      this.handleAlreadyLoadedImages();
      
      // Escuchar contenido dinámico
      this.listenForDynamicContent();
    }

    checkWebPSupport() {
      const webp = new Image();
      webp.onload = webp.onerror = () => {
        if (webp.height === 2) {
          document.documentElement.classList.add('webp');
        }
      };
      webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    }

    initIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadImage(entry.target);
              this.imageObserver.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '50px 0px', // Reducir margen para mobile performance
          threshold: 0.1 // Menos sensible para reducir carga
        });
      }
    }

    loadImage(img) {
      // Si la imagen ya tiene src y está cargando lazy, solo remover clases de carga
      if (img.src && img.loading === 'lazy') {
        img.classList.add('loaded');
        img.classList.remove('lazy-image-placeholder');
        
        // Remover shimmer de carga si está presente
        const shimmerContainer = img.closest('.loading-shimmer');
        if (shimmerContainer) {
          shimmerContainer.classList.remove('loading-shimmer');
        }
        
        // Disparar evento personalizado
        img.dispatchEvent(new CustomEvent('lazy:loaded', {
          detail: { image: img }
        }));
        return;
      }

      // Si tiene dataset.src (para imágenes que usan lazy loading manual)
      if (img.dataset.src) {
        // Crear nueva imagen para precargar
        const imageLoader = new Image();
        
        imageLoader.onload = () => {
          // Aplicar la imagen cargada
          img.src = img.dataset.src;
          img.classList.add('loaded');
          img.classList.remove('lazy-image-placeholder');
          
          // Remover shimmer de carga si está presente
          const shimmerContainer = img.closest('.loading-shimmer');
          if (shimmerContainer) {
            shimmerContainer.classList.remove('loading-shimmer');
          }
          
          // Disparar evento personalizado
          img.dispatchEvent(new CustomEvent('lazy:loaded', {
            detail: { image: img }
          }));
        };

        imageLoader.onerror = () => {
          console.warn('Error al cargar imagen:', img.dataset.src);
          img.classList.add('error');
        };

        // Comenzar carga
        imageLoader.src = img.dataset.src;
      }
    }

    handleAlreadyLoadedImages() {
      // Remover clases de carga de imágenes que ya están cargadas
      const images = document.querySelectorAll('img[loading="lazy"]');
      images.forEach(img => {
        if (img.complete && img.naturalHeight !== 0) {
          img.classList.add('loaded');
          img.classList.remove('lazy-image-placeholder');
          const shimmerContainer = img.closest('.loading-shimmer');
          if (shimmerContainer) {
            shimmerContainer.classList.remove('loading-shimmer');
          }
        } else if (img.src) {
          // Si la imagen tiene src pero aún no está cargada, agregar listener
          img.addEventListener('load', () => {
            img.classList.add('loaded');
            img.classList.remove('lazy-image-placeholder');
            const shimmerContainer = img.closest('.loading-shimmer');
            if (shimmerContainer) {
              shimmerContainer.classList.remove('loading-shimmer');
            }
          });
        }
      });
    }

    listenForDynamicContent() {
      // Escuchar eventos del tema que puedan agregar nuevas imágenes
      document.addEventListener('theme:section:load', () => {
        this.observeNewImages();
      });

      document.addEventListener('theme:cart:change', () => {
        this.observeNewImages();
      });
    }

    observeNewImages() {
      const lazyImages = document.querySelectorAll('img[loading="lazy"]:not([data-lazy-observed])');
      lazyImages.forEach(img => {
        img.setAttribute('data-lazy-observed', 'true');
        if (this.imageObserver) {
          this.imageObserver.observe(img);
        }
      });
    }

    // Método público para cargar manualmente imágenes críticas
    loadCriticalImages() {
      const criticalImages = document.querySelectorAll('img[data-critical="true"]');
      criticalImages.forEach(img => {
        this.loadImage(img);
      });
    }
  }

  // ===== INICIALIZACIÓN DEL SISTEMA =====
  
  // Inicializar lazy loading cuando el DOM esté listo
  document.addEventListener('DOMContentLoaded', () => {
    window.lazyImageLoader = new LazyImageLoader();
    
    // Cargar imágenes críticas inmediatamente
    window.lazyImageLoader.loadCriticalImages();
    
    // Asegurar que todas las imágenes con src sean visibles
    setTimeout(() => {
      const imagesWithSrc = document.querySelectorAll('img[src].lazy-image');
      imagesWithSrc.forEach(img => {
        img.classList.add('loaded');
        img.classList.remove('lazy-image-placeholder');
        const shimmerContainer = img.closest('.loading-shimmer');
        if (shimmerContainer) {
          shimmerContainer.classList.remove('loading-shimmer');
        }
      });
    }, 100);
  });

  // ===== OPTIMIZACIONES ESPECÍFICAS =====
  
  // Optimizar carga de imágenes para grids de productos
  document.addEventListener('theme:variant:change', function(event) {
    const variant = event.detail.variant;
    const container = event.target;
    
    if (variant && container) {
      // Precargar imágenes de variantes
      const variantImages = container.querySelectorAll('img[data-variant-image]');
      variantImages.forEach(img => {
        if (img.dataset.src && !img.src) {
          window.lazyImageLoader.loadImage(img);
        }
      });
    }
  });

  // Precargar imágenes al hacer hover para mejor UX
  document.addEventListener('mouseover', (e) => {
    const productCard = e.target.closest('.product-grid-item');
    if (productCard) {
      const lazyImages = productCard.querySelectorAll('img[loading="lazy"]:not([data-preloaded])');
      lazyImages.forEach(img => {
        img.setAttribute('data-preloaded', 'true');
        if (img.dataset.src && !img.src) {
          window.lazyImageLoader.loadImage(img);
        }
      });
    }
  });

  // ===== OPTIMIZACIONES ADICIONALES =====
  
  // Mejorar rendimiento en scroll
  let scrollTimeout;
  document.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      // Forzar observación de nuevas imágenes durante scroll rápido
      if (window.lazyImageLoader) {
        window.lazyImageLoader.observeNewImages();
      }
    }, 100);
  });

  // Optimizar para dispositivos móviles
  if ('ontouchstart' in window) {
    // En dispositivos táctiles, precargar imágenes más temprano
    document.addEventListener('touchstart', (e) => {
      const img = e.target.closest('img[loading="lazy"]');
      if (img && img.dataset.src && !img.src) {
        window.lazyImageLoader.loadImage(img);
      }
    });
  }

  // ===== LAZY LOADING DE VÍDEOS =====
  
  class VideoLazyLoader {
    constructor() {
      this.videoObserver = null;
      this.loadedVideos = new Set();
      this.init();
    }

    init() {
      this.initVideoObserver();
      this.handleExistingVideos();
    }

    initVideoObserver() {
      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Cargar vídeo automáticamente cuando aparece en scroll
              const iframe = entry.target.querySelector('.video-iframe');
              const thumbnail = entry.target.querySelector('.video-thumbnail');
              const playButton = entry.target.querySelector('.video-play-button');
              this.loadVideo(entry.target, iframe, thumbnail, playButton);
              this.videoObserver.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '200px 0px', // Cargar vídeo más tarde para mobile performance
          threshold: 0.2 // Menos sensible para reducir carga inicial
        });
      }
    }

    handleExistingVideos() {
      const videoContainers = document.querySelectorAll('.video-lazy-container');
      videoContainers.forEach(container => {
        this.videoObserver.observe(container);
      });
    }

    prepareVideo(container) {
      // Ya no es necesario preparar eventos de click
      // Los vídeos se cargan automáticamente cuando aparecen en scroll
      return;
    }

    loadVideo(container, iframe, thumbnail, playButton) {
      if (this.loadedVideos.has(container)) return;
      
      this.loadedVideos.add(container);
      
      // Verificar si el vídeo tiene autoplay configurado
      const src = iframe.dataset.src;
      const hasAutoplay = src && src.includes('autoplay=1');
      
      // Mostrar estado de carga
      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'video-loading';
      loadingDiv.textContent = 'Cargando vídeo...';
      container.appendChild(loadingDiv);

      if (src) {
        iframe.src = src;
        
        iframe.onload = () => {
          // Ocultar loading y mostrar vídeo
          loadingDiv.remove();
          iframe.classList.add('loaded');
          container.classList.add('loaded');
          
          // Ocultar thumbnail y play button
          if (thumbnail) {
            thumbnail.style.opacity = '0';
          }
          if (playButton) {
            playButton.style.opacity = '0';
          }
          
          // Si tiene autoplay, iniciar reproducción automáticamente
          if (hasAutoplay) {
            // Pequeño delay para asegurar que el iframe esté listo
            setTimeout(() => {
              try {
                // Para YouTube
                if (src.includes('youtube.com')) {
                  // YouTube autoplay funciona automáticamente con el parámetro
                  console.log('YouTube autoplay iniciado');
                }
                // Para Vimeo
                else if (src.includes('vimeo.com')) {
                  // Vimeo autoplay funciona automáticamente con el parámetro
                  console.log('Vimeo autoplay iniciado');
                }
              } catch (error) {
                console.warn('Error al iniciar autoplay:', error);
              }
            }, 500);
          }
          
          // Disparar evento personalizado
          container.dispatchEvent(new CustomEvent('video:loaded', {
            detail: { container, iframe, hasAutoplay }
          }));
        };

        iframe.onerror = () => {
          loadingDiv.textContent = 'Error al cargar vídeo';
          console.warn('Error al cargar vídeo:', src);
        };
      }
    }

    // Método público para cargar vídeo específico
    loadVideoById(containerId) {
      const container = document.getElementById(containerId);
      if (container) {
        this.prepareVideo(container);
        const iframe = container.querySelector('.video-iframe');
        const thumbnail = container.querySelector('.video-thumbnail');
        const playButton = container.querySelector('.video-play-button');
        this.loadVideo(container, iframe, thumbnail, playButton);
      }
    }
  }

  // Inicializar lazy loading de vídeos
  document.addEventListener('DOMContentLoaded', () => {
    window.videoLazyLoader = new VideoLazyLoader();
  });

  // ===== LAZY LOADING DE SECCIONES =====
  
  // Lazy load de secciones pesadas (collection sliders, etc.)
  class SectionLazyLoader {
    constructor() {
      this.sectionObserver = null;
      this.init();
    }

    init() {
      if ('IntersectionObserver' in window) {
        this.sectionObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadSection(entry.target);
              this.sectionObserver.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '200px 0px', // Cargar secciones antes de que sean visibles
          threshold: 0.01
        });
      }
      
      // Observar secciones existentes
      this.observeSections();
      
      // Escuchar nuevas secciones dinámicas
      document.addEventListener('theme:section:load', () => {
        this.observeSections();
      });
    }

    observeSections() {
      const lazySections = document.querySelectorAll('[data-lazy-section="true"]:not([data-lazy-loaded])');
      lazySections.forEach(section => {
        if (this.sectionObserver) {
          this.sectionObserver.observe(section);
        }
      });
    }

    loadSection(section) {
      // Marcar como cargada
      section.setAttribute('data-lazy-loaded', 'true');
      
      // Forzar carga de imágenes lazy dentro de la sección
      const lazyImages = section.querySelectorAll('img[loading="lazy"]:not([data-preloaded])');
      lazyImages.forEach(img => {
        if (img.dataset.src && !img.src) {
          if (window.lazyImageLoader) {
            window.lazyImageLoader.loadImage(img);
          }
        }
      });
      
      // Disparar evento personalizado
      section.dispatchEvent(new CustomEvent('section:lazy:loaded', {
        detail: { section }
      }));
    }
  }

  // Inicializar lazy loading de secciones
  document.addEventListener('DOMContentLoaded', () => {
    window.sectionLazyLoader = new SectionLazyLoader();
  });

  // ===== UTILIDADES =====
  
  // Función para precargar imagen específica
  window.preloadImage = function(imageSrc) {
    const img = new Image();
    img.src = imageSrc;
    return img;
  };

  // Función para verificar si imagen está cargada
  window.isImageLoaded = function(img) {
    return img.complete && img.naturalHeight !== 0;
  };

  // Función para cargar vídeo específico
  window.loadVideo = function(containerId) {
    if (window.videoLazyLoader) {
      window.videoLazyLoader.loadVideoById(containerId);
    }
  };

})();
